<template>
  <header v-if="!fromDetailTemplate">
    <h3>Agrega usuarios a la secuencia</h3>
    <p>
      Elige qué usuarios atenderán a tus contactos cuando respondan alguna de las acciones
      configuradas.
    </p>
  </header>

  <a-input-search v-model:value="search" placeholder="Buscar por nombre" style="width: 100%" />

  <div class="list">
    <template v-if="loading">
      <a-skeleton-button
        v-for="i in 5"
        :key="i"
        block
        size="large"
        active
        style="margin-bottom: 1rem"
      />
    </template>

    <span class="info" v-else-if="!orderedUsers.length">No se encontraron usuarios</span>

    <a-checkbox-group
      v-model:value="selectedUsers"
      @update:value="handleChange"
      :disabled="disabled"
    >
      <template v-for="user in orderedUsers" :key="user.id">
        <a-tooltip placement="right">
          <template #title v-if="isUserDisabled(user)">
            Este usuario ya está asignado a una secuencia activa de la misma campaña. 
            Para seleccionarlo, inactiva la secuencia actual.
          </template>
          <a-checkbox 
            :value="user.id" 
            :disabled="isUserDisabled(user)"
          >
            <div class="user-content">
              <UserAvatar :record="{ user }" />
              <strong class="name">{{ user.fullname }}</strong>

              <span class="queues">
                {{ user.queues.length === 1 ? "1 cola" : `${user.queues.length} colas` }}

                <a-popover :title="user.queues.length === 1 ? '1 cola' : `${user.queues.length} colas`">
                  <template #content>
                    <ul>
                      <li v-for="queue in user.queues" :key="queue.id">{{ queue.name }}</li>
                    </ul>
                  </template>

                  <InfoCircleOutlined style="margin-left: 12px" />
                </a-popover>
              </span>
            </div>
          </a-checkbox>
        </a-tooltip>
      </template>
    </a-checkbox-group>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";

import { UserData } from "@/@types/global";
import fetchPortfolioUsers from "@/app/queues/services/requests/fetchAgentsQueue";
import { InfoCircleOutlined } from "@ant-design/icons-vue";
import { UserStepDto } from "@/@types/flows/sequences/dto";
import { Sequence } from "@/@types/flows/sequences";
import { cloneDeep, isEqual } from "lodash";

import UserAvatar from "@/app/shared/components/avatar/UserAvatar.vue";

const props = defineProps<{
  fromDetailTemplate?: boolean;
  sequence?: Sequence;
  disabled?: boolean;
  portfolioId?: number;
}>();

const emit = defineEmits<{
  toggleButton: [disabled: boolean];
}>();

const search = ref("");
const users = ref<(UserData & { checked: boolean; sequence_id: number })[]>([]);
const selectedUsers = ref<number[]>(props.sequence?.users || []);
const comparisonUsers = cloneDeep(selectedUsers.value);

const loading = ref(false);

const isUserDisabled = (user: UserData & { sequence_id: number }) =>
  props.sequence
    ? user.sequence_id
      ? props.sequence.id !== user.sequence_id
      : false
    : user.sequence_id;

const fetchUsers = async () => {
  if (!props.portfolioId) return;

  loading.value = true;
  selectedUsers.value = props.sequence?.users || [];
  const response = await fetchPortfolioUsers(props.portfolioId, { sequences: true });
  users.value = response.map((user: UserData) => ({
    ...user,
    checked: selectedUsers.value.includes(user.id),
  }));

  loading.value = false;
};

onMounted(async () => {
  await fetchUsers();
});

const handleChange = (selected: number[]) => {
  users.value = users.value.map((user) => ({ ...user, checked: selected.includes(user.id) }));
};

watch(selectedUsers, () => {
  const isDisabled = isEqual(selectedUsers.value, comparisonUsers);

  emit("toggleButton", isDisabled);
});

watch(
  () => props.portfolioId,
  async () => {
    await fetchUsers();
  }
);

const orderedUsers = computed(() => {
  const filtered = users.value.filter((user) =>
    user.fullname?.toLowerCase().includes(search.value.toLowerCase())
  );

  const checkedFiltered = filtered.filter((f) => f.checked);
  const uncheckedFiltered = filtered.filter((f) => !f.checked);

  return [...checkedFiltered, ...uncheckedFiltered];
});

const getFormState = (): UserStepDto => ({ users: selectedUsers.value });

defineExpose({
  getFormState,
  formRef: {
    validate() {
      console.log("user validation");
    },
  },
});
</script>

<style scoped lang="sass">
h3
  color: $gray-9
  font-size: 20px
  margin-bottom: 16px

h4
  color: $gray-9
  font-size: 14px
  margin-bottom: 16px
  font-weight: 600

.divider
  background-color: $gray-6

header
  width: 100%
  text-align: center
  margin-bottom: 25px

  p
    color: $text-secondary
    margin: 0
    font-size: 16px

.list
  margin-top: 16px
  overflow-y: auto
  max-height: calc(100% - 200px)

.user-content
  display: grid
  grid-template-columns: 40px 200px 180px
  align-items: center
  gap: 8px

  .name
    color: $text-primary
    max-width: 200px
    text-overflow: ellipsis
    white-space: nowrap
    overflow: hidden
    text-align: left

  .queues
    width: 100%
    text-align: right
    color: $text-tertiary

:deep(.ant-checkbox-group)
  width: 100%
  margin-bottom: 12px
  display: block

:deep(.ant-checkbox-wrapper)
  border-radius: 10px
  border: 1px solid rgba(0, 0, 0, 0.15)
  padding: 12px
  display: flex
  align-items: center
  margin: 0
  margin-bottom: 12px
  flex-direction: row-reverse
  justify-content: space-between

:deep(.ant-checkbox-wrapper::after)
  display: none
</style>
